// =============================================================================
// FORM CANVAS - Main form editing area
// =============================================================================

import { useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { FormFieldCard } from './FormFieldCard';
import { useFormBuilderStore } from '../store';
import { cn } from '../../../lib/utils';

export function FormCanvas() {
  const {
    sections,
    selectedFieldId,
    addSection,
    addField,
    moveField,
    selectField,
  } = useFormBuilderStore();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      if (activeId === overId) return;

      // Find which sections contain the fields
      let fromSectionId: string | null = null;
      let toSectionId: string | null = null;
      let fromIndex = -1;
      let toIndex = -1;

      for (const section of sections) {
        const activeIndex = section.fields.findIndex((f) => f.id === activeId);
        const overIndex = section.fields.findIndex((f) => f.id === overId);

        if (activeIndex !== -1) {
          fromSectionId = section.id;
          fromIndex = activeIndex;
        }
        if (overIndex !== -1) {
          toSectionId = section.id;
          toIndex = overIndex;
        }
      }

      if (fromSectionId && toSectionId && fromIndex !== -1 && toIndex !== -1) {
        moveField(fromSectionId, toSectionId, fromIndex, toIndex);
      }
    },
    [sections, moveField]
  );

  const handleCanvasClick = () => {
    selectField(null);
  };

  return (
    <div
      className="flex-1 overflow-y-auto p-8 bg-secondary-50 dark:bg-secondary-950"
      onClick={handleCanvasClick}
    >
      <div className="max-w-3xl mx-auto space-y-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {sections.map((section) => (
            <div
              key={section.id}
              className={cn(
                'rounded-xl border border-secondary-200 bg-white p-6',
                'dark:border-secondary-700 dark:bg-secondary-800'
              )}
            >
              {/* Section header */}
              {section.title && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                    {section.title}
                  </h3>
                  {section.description && (
                    <p className="text-sm text-secondary-500 mt-1">
                      {section.description}
                    </p>
                  )}
                </div>
              )}

              {/* Fields */}
              <SortableContext
                items={section.fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {section.fields.map((field) => (
                    <FormFieldCard
                      key={field.id}
                      field={field}
                      sectionId={section.id}
                      isSelected={selectedFieldId === field.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectField(field.id);
                      }}
                    />
                  ))}
                </div>
              </SortableContext>

              {/* Add field button */}
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    addField(section.id, 'text');
                  }}
                  className="border-dashed"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Field
                </Button>
              </div>
            </div>
          ))}
        </DndContext>

        {/* Add section button */}
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => addSection()}
            className="border-dashed"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </Button>
        </div>
      </div>
    </div>
  );
}
